/**
 * api.js — API Service Layer for CAI
 *
 * Phase 1 Integration COMPLETE:
 *   sendMessage() now calls the real FastAPI backend at localhost:8000.
 *   authenticate() remains mock — backend auth endpoint (Phase 2) not built yet.
 *
 * Base URL configuration:
 *   Development : http://localhost:8000  (your local FastAPI server)
 *   Production  : Set REACT_APP_API_URL env variable before npm build
 */

import logger from './logger';

// ─── Configuration ────────────────────────────────────────────────────────────

// Reads from .env file if REACT_APP_API_URL is set, otherwise defaults to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ─── Module-level auth store ──────────────────────────────────────────────────
//
// Holds the current access + refresh tokens and two callbacks that AuthContext
// registers after login.  This allows fetchWithAuth() to silently refresh the
// access token and retry a failed request without involving React state.
//
// AuthContext calls initApiAuth() after every login/session-restore and
// clearApiAuth() on logout.  No other code should write to these variables.

let _accessToken    = null;   // Current JWT access token
let _refreshToken   = null;   // Current JWT refresh token (null for forced-reset sessions)
let _onTokenRefresh = null;   // (newAccessToken) => void — AuthContext updates React state
let _onSessionEnd   = null;   // () => void — AuthContext triggers logout

/**
 * initApiAuth() — Called by AuthContext after login or session restore.
 * Registers tokens and lifecycle callbacks so fetchWithAuth can self-manage.
 *
 * @param {string}   accessToken   - JWT access token
 * @param {string}   refreshToken  - JWT refresh token (null for forced-reset)
 * @param {function} onTokenRefresh - called with (newToken) when refresh succeeds
 * @param {function} onSessionEnd   - called when refresh fails → triggers full logout
 */
export function initApiAuth(accessToken, refreshToken, onTokenRefresh, onSessionEnd) {
  _accessToken    = accessToken;
  _refreshToken   = refreshToken;
  _onTokenRefresh = onTokenRefresh;
  _onSessionEnd   = onSessionEnd;
  logger.info('Api: Auth store initialised', { hasRefresh: !!refreshToken });
}

/**
 * clearApiAuth() — Called by AuthContext on logout.
 * Clears all stored tokens and callbacks.
 */
export function clearApiAuth() {
  _accessToken    = null;
  _refreshToken   = null;
  _onTokenRefresh = null;
  _onSessionEnd   = null;
  logger.info('Api: Auth store cleared');
}

// ─── Internal: call the refresh endpoint ─────────────────────────────────────

async function _doRefresh() {
  if (!_refreshToken) return null;
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ refresh_token: _refreshToken }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.token || null;
  } catch {
    return null;
  }
}

// ─── Centralised authenticated fetch ─────────────────────────────────────────
//
// ALL authenticated API calls should go through fetchWithAuth().
//
// What it does automatically:
//   1. Injects Authorization header using the module-level access token.
//   2. On 401: attempts to refresh the access token via POST /auth/refresh.
//        - If refresh succeeds: updates the module token, calls onTokenRefresh
//          so AuthContext keeps React state in sync, then retries the original
//          request once with the new token.
//        - If refresh fails (token expired / revoked): calls onSessionEnd so
//          AuthContext triggers logout, then throws SESSION_EXPIRED.
//   3. Network failure → throws NETWORK_ERROR.
//
// The `token` parameter is kept for backward compatibility with existing callers
// that pass it explicitly (e.g. sendMessage).  When provided it overrides the
// module-level token for that call only — useful for special flows like the
// change-password page which has a deliberately short-lived token.
//
// @param {string} url      - Full URL
// @param {object} options  - fetch options (method, body, extra headers)
// @param {string} [token]  - Optional override; defaults to module-level token
// @returns {Promise<Response>}
export async function fetchWithAuth(url, options = {}, token = null) {
  const activeToken = token || _accessToken;

  const buildHeaders = (t) => ({
    'Content-Type': 'application/json',
    ...options.headers,
    ...(t ? { 'Authorization': `Bearer ${t}` } : {}),
  });

  // ── First attempt ──────────────────────────────────────────────────────────
  let response;
  try {
    response = await fetch(url, { ...options, headers: buildHeaders(activeToken) });
  } catch (networkErr) {
    logger.error('fetchWithAuth: Network failure', { url, error: networkErr.message });
    const err = new Error('Cannot connect to Querely backend. Check your network connection.');
    err.code  = 'NETWORK_ERROR';
    throw err;
  }

  // Not a 401 — return as-is (caller handles non-auth errors)
  if (response.status !== 401) return response;

  // ── 401 received — attempt silent token refresh ────────────────────────────
  logger.warn('fetchWithAuth: 401 received — attempting token refresh', { url });

  const newToken = await _doRefresh();

  if (newToken) {
    // Refresh succeeded — update module state and notify AuthContext
    _accessToken = newToken;
    if (_onTokenRefresh) _onTokenRefresh(newToken);
    logger.info('fetchWithAuth: Token refreshed — retrying original request');

    // Retry the original request once with the new token
    try {
      return await fetch(url, { ...options, headers: buildHeaders(newToken) });
    } catch (networkErr) {
      const err = new Error('Cannot connect to Querely backend. Check your network connection.');
      err.code  = 'NETWORK_ERROR';
      throw err;
    }
  }

  // Refresh failed — session is truly over
  logger.warn('fetchWithAuth: Token refresh failed — ending session', { url });
  if (_onSessionEnd) _onSessionEnd();

  const expired = new Error('Your session has expired. Please log in again.');
  expired.code  = 'SESSION_EXPIRED';
  throw expired;
}

// Simulated network delay (ms) — used only for mock functions (auth/logout)
const MOCK_DELAY_MS = 800;

// ─── Mock Data — Realistic SAP responses ─────────────────────────────────────

const MOCK_RESPONSES = [
  {
    keywords: ['purchase order', 'po', 'purchase orders'],
    module: 'MM',
    type: 'table',
    summary: 'Found **5 open Purchase Orders** matching your query.',
    tableData: {
      headers: ['PO Number', 'Vendor', 'Material', 'Qty', 'Value (₹)', 'Status'],
      rows: [
        ['4500012301', 'Tata Consultancy', 'Raw Material A', '500 EA', '2,45,000', 'Open'],
        ['4500012289', 'Infosys Ltd', 'Component B-12', '120 KG', '98,400',  'Partially Delivered'],
        ['4500012277', 'Wipro Hardware', 'Spare Part X9', '50 EA',  '1,12,500', 'Open'],
        ['4500012265', 'HCL Systems',   'Office Supplies', '200 EA', '34,600',  'Open'],
        ['4500012250', 'Tech Mahindra', 'Server Unit Z',  '2 EA',   '8,90,000', 'In Approval'],
      ],
    },
  },
  {
    keywords: ['sales order', 'so', 'sales orders'],
    module: 'SD',
    type: 'table',
    summary: 'Found **4 Sales Orders** for the selected period.',
    tableData: {
      headers: ['SO Number', 'Customer', 'Material', 'Qty', 'Net Value (₹)', 'Delivery Date'],
      rows: [
        ['5100089201', 'ABC Corporation',  'Product Alpha',  '1000 EA', '15,50,000', '15-Mar-2026'],
        ['5100089198', 'XYZ Industries',   'Product Beta',   '250 EA',  '4,87,500',  '20-Mar-2026'],
        ['5100089190', 'Global Traders',   'Product Gamma',  '500 EA',  '9,25,000',  '25-Mar-2026'],
        ['5100089185', 'Prime Exports Ltd','Product Delta',  '150 EA',  '3,15,000',  '30-Mar-2026'],
      ],
    },
  },
  {
    keywords: ['invoice', 'vendor invoice', 'invoices', 'payment'],
    module: 'FI',
    type: 'table',
    summary: 'Found **3 pending Vendor Invoices** awaiting payment.',
    tableData: {
      headers: ['Invoice No', 'Vendor', 'Amount (₹)', 'Due Date', 'Days Overdue', 'Status'],
      rows: [
        ['5105678901', 'Tata Consultancy', '2,45,000', '01-Mar-2026', '6 days',  'Overdue'],
        ['5105678890', 'Infosys Ltd',      '98,400',   '05-Mar-2026', '2 days',  'Overdue'],
        ['5105678875', 'Wipro Hardware',   '1,12,500', '15-Mar-2026', 'Due soon','Pending'],
      ],
    },
  },
  {
    keywords: ['stock', 'inventory', 'material stock', 'stock level'],
    module: 'MM',
    type: 'table',
    summary: 'Current stock levels retrieved from Plant 1000.',
    tableData: {
      headers: ['Material', 'Description', 'Plant', 'Storage Loc', 'Unrestricted Stock', 'Unit'],
      rows: [
        ['MAT-10045', 'Raw Material A',  '1000', 'SL01', '1,250', 'EA'],
        ['MAT-10046', 'Component B-12',  '1000', 'SL01', '340',   'KG'],
        ['MAT-10047', 'Spare Part X9',   '1000', 'SL02', '78',    'EA'],
        ['MAT-10048', 'Office Supplies', '1000', 'SL03', '560',   'EA'],
      ],
    },
  },
  {
    keywords: ['hello', 'hi', 'help', 'what can you do', 'capabilities'],
    module: null,
    type: 'text',
    text: `Hello! I'm **Querely** — your AI assistant for SAP, by InqLect.

I can help you with:

• **MM — Materials Management**: Purchase Orders, Stock Levels, Goods Receipt, Vendor details
• **SD — Sales & Distribution**: Sales Orders, Customer data, Deliveries, Pricing
• **FI — Finance**: Vendor Invoices, Payment runs, Account balances, Cost centres
• **PP — Production Planning**: Production Orders, MRP, Capacity planning
• **ITSM**: Incidents, Change Requests, Service Tickets

Just ask me in plain English — for example:
*"Show me all open purchase orders from vendor Tata"*
*"What is the stock level for material MAT-10045?"*
*"Display overdue invoices for this month"*`,
  },
];

// ─── Helper: Find best mock response for a query ─────────────────────────────

function findMockResponse(query) {
  const lower = query.toLowerCase();

  for (const response of MOCK_RESPONSES) {
    if (response.keywords.some((kw) => lower.includes(kw))) {
      return response;
    }
  }

  // Default fallback response
  return {
    module: null,
    type: 'text',
    text: `I understood your query: *"${query}"*

**[DEMO MODE]** — In the live product, this would:
1. Send your query to the AI Engine (Python FastAPI)
2. The AI identifies the SAP module and extracts key parameters
3. The backend calls your SAP OData API
4. Results are formatted and returned here

Try asking about:
- Purchase Orders
- Sales Orders
- Vendor Invoices
- Stock Levels`,
  };
}

// ─── Public API Functions ─────────────────────────────────────────────────────

/**
 * authenticate() — Logs a user in and returns a real JWT token.
 *
 * Calls: POST /auth/login
 * Backend validates credentials against PostgreSQL, returns signed JWT.
 * JWT contains tenant_id — used by backend to isolate SAP data per company.
 *
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{token: string, user: object}>}
 */
export async function authenticate(email, password) {
  logger.info('Auth: Login attempt', { email });

  if (!email || !password) {
    throw new Error('Email and password are required.');
  }
  if (!email.includes('@')) {
    throw new Error('Please enter a valid email address.');
  }
  if (password.length < 4) {
    throw new Error('Password must be at least 4 characters.');
  }

  let response;
  try {
    response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
    });
  } catch (networkErr) {
    logger.error('Auth: Cannot reach backend', networkErr);
    throw new Error('Cannot connect to Querely backend. Make sure the server is running.');
  }

  if (!response.ok) {
    let errMsg = 'Login failed. Please try again.';
    try {
      const errData = await response.json();
      errMsg = errData.detail || errData.error || errMsg;
    } catch (_) {}
    logger.warn('Auth: Login failed', { status: response.status });
    throw new Error(errMsg);
  }

  const data = await response.json();
  logger.info('Auth: Login successful', { userId: data.user?.id, tenant: data.user?.tenant_id });
  // refresh_token is null for forced-reset sessions (must_change_password=true)
  return { token: data.token, refreshToken: data.refresh_token || null, user: data.user, brand: data.brand };
}

/**
 * sendMessage() — Sends a user query to the real FastAPI backend.
 *
 * Calls: POST http://localhost:8000/api/v1/chat/query
 *
 * Backend expects:
 *   { query: string, conversation_id: string }
 *
 * Backend returns:
 *   { type, status, text, summary, table_data, module, processing_time_ms, ... }
 *
 * We map backend snake_case to frontend camelCase before returning:
 *   table_data → tableData
 *
 * @param {string} query          - User's natural language query
 * @param {string} conversationId - Current conversation ID for context
 * @param {string} token          - JWT auth token (required — backend returns 401 if missing)
 * @returns {Promise<object>}     - Mapped response ready for ChatPage/ChatMessage
 */
export async function sendMessage(query, conversationId, token) {
  logger.info('Chat: Sending query to backend', { query: query?.slice(0, 80), conversationId });

  // ── Input validation (mirrors backend validation) ──────────────────────────
  if (!query || query.trim().length === 0) {
    throw new Error('Query cannot be empty.');
  }
  if (query.length > 500) {
    throw new Error('Query is too long. Please keep it under 500 characters.');
  }

  // ── Call backend via centralised auth wrapper ──────────────────────────────
  // fetchWithAuth handles: Authorization header, 401 → SESSION_EXPIRED, network errors
  let response;
  try {
    response = await fetchWithAuth(
      `${API_BASE_URL}/api/v1/chat/query`,
      {
        method: 'POST',
        body: JSON.stringify({
          query:           query.trim(),
          conversation_id: conversationId,
        }),
      },
      token,
    );
  } catch (err) {
    // Re-throw SESSION_EXPIRED and NETWORK_ERROR as-is so ChatPage can handle them
    throw err;
  }

  // ── Handle non-401 HTTP errors from backend ────────────────────────────────
  if (!response.ok) {
    let errMsg = `Backend error (HTTP ${response.status})`;
    try {
      // Backend always returns { error, code, details } shape on errors
      const errData = await response.json();
      errMsg = errData.error || errData.detail || errMsg;
    } catch (_) {
      // Could not parse error body — use status code message
    }
    logger.error('Chat: Backend returned error', { status: response.status, errMsg });
    throw new Error(errMsg);
  }

  // ── Parse successful response ──────────────────────────────────────────────
  const data = await response.json();
  logger.info('Chat: Response received from backend', {
    module: data.module,
    type:   data.type,
    status: data.status,
    ms:     data.processing_time_ms,
  });

  // ── Map backend response to frontend shape ─────────────────────────────────
  // Key difference: backend uses snake_case (table_data), frontend uses camelCase (tableData)
  return {
    type:        data.type,             // 'text' | 'table' | 'error' — same in both
    text:        data.text,             // For text/error messages
    summary:     data.summary,          // Summary line above the table
    tableData:   data.table_data,       // TABLE DATA: snake → camel mapping here
    module:      data.module,           // 'MM' | 'SD' | 'FI' | 'PP' | 'EWM' | 'ITSM'
    suggestions: data.suggestions || [],// Follow-up question suggestions
  };
}

/**
 * logout() — Blacklists both the access token and refresh token on the backend.
 *
 * Calls: POST /auth/logout
 * Sends refresh_token in the body so the backend can blacklist it too.
 * Frontend clears state regardless of whether the API call succeeds.
 *
 * @param {string}      token        - JWT access token
 * @param {string|null} refreshToken - JWT refresh token (null for forced-reset sessions)
 */
export async function logout(token, refreshToken = null) {
  logger.info('Auth: Logging out');
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      // Send refresh token in body so backend can blacklist it too
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch (_) {
    // Don't block logout if backend unreachable — frontend clears state anyway
    logger.warn('Auth: Logout API call failed — clearing local session anyway');
  }
}

// ─── User Management (tenant admin) ──────────────────────────────────────────

export async function listUsers(token) {
  const res = await fetch(`${API_BASE_URL}/users`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to load users');
  return res.json();
}

export async function createUser(token, body) {
  const res = await fetch(`${API_BASE_URL}/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || data.error || 'Failed to create user');
  return data;
}

export async function updateUser(token, userId, body) {
  const res = await fetch(`${API_BASE_URL}/users/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || data.error || 'Failed to update user');
  return data;
}

export async function deleteUser(token, userId) {
  const res = await fetch(`${API_BASE_URL}/users/${userId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || data.error || 'Failed to delete user');
  }
}

export async function deactivateUser(token, userId) {
  const res = await fetch(`${API_BASE_URL}/users/${userId}/deactivate`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
  return res.json();
}

export async function activateUser(token, userId) {
  const res = await fetch(`${API_BASE_URL}/users/${userId}/activate`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
  return res.json();
}

// ─── Profile ──────────────────────────────────────────────────────────────────

export async function getMyProfile(token) {
  const res = await fetch(`${API_BASE_URL}/users/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load profile');
  return res.json();
}

export async function updateMyProfile(token, body) {
  const res = await fetch(`${API_BASE_URL}/users/me`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || data.error || 'Failed to update profile');
  return data;
}

export async function changePassword(token, currentPassword, newPassword) {
  const res = await fetch(`${API_BASE_URL}/users/me/password`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || data.error || 'Failed to change password');
  return data;
}

// ─── Conversation History ─────────────────────────────────────────────────────

/**
 * listConversations() — Returns the 20 most recent conversations for the current user.
 * Used by the Sidebar to populate the "Recent" section.
 */
export async function listConversations(token) {
  const res = await fetch(`${API_BASE_URL}/chat/conversations`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load conversations');
  return res.json(); // Array of { conversation_id, title, created_at, updated_at }
}

/**
 * getConversation() — Fetches the full message list for one conversation.
 * Used when the user clicks a past conversation in the sidebar.
 *
 * @returns {Promise<{conversation_id, title, messages: [{role, content, created_at}]}>}
 */
export async function getConversation(token, conversationId) {
  const res = await fetch(`${API_BASE_URL}/chat/conversations/${conversationId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load conversation');
  return res.json();
}

/**
 * deleteConversation() — Permanently deletes a conversation and all its messages.
 */
export async function deleteConversation(token, conversationId) {
  const res = await fetch(`${API_BASE_URL}/chat/conversations/${conversationId}`, {
    method:  'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to delete conversation');
  }
}

// ─── Password Reset ───────────────────────────────────────────────────────────

/**
 * requestPasswordReset() — Step 1: submit email to receive OTP.
 * Returns { message, mode } where mode is "email" or "admin".
 * No auth required.
 */
export async function requestPasswordReset(email) {
  const res = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || data.error || 'Failed to send reset code');
  return data; // { message, mode }
}

/**
 * verifyResetOTP() — Step 2: submit email + 6-digit code.
 * Returns { reset_token } on success.
 * No auth required.
 */
export async function verifyResetOTP(email, otp) {
  const res = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email, otp }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || data.error || 'Invalid code');
  return data; // { reset_token }
}

/**
 * resetPassword() — Step 3: submit reset_token + new_password.
 * No auth required.
 */
export async function resetPassword(resetToken, newPassword) {
  const res = await fetch(`${API_BASE_URL}/auth/reset-password`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ reset_token: resetToken, new_password: newPassword }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || data.error || 'Failed to reset password');
  return data; // { message }
}

/**
 * forceResetUserPassword() — Admin sets a temporary password for a user.
 * User will be flagged to change password on next login.
 */
export async function forceResetUserPassword(token, userId, tempPassword) {
  const res = await fetch(`${API_BASE_URL}/users/${userId}/force-reset`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body:    JSON.stringify({ temp_password: tempPassword }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || data.error || 'Failed to reset password');
  return data;
}

/**
 * getResetMode() — Returns current password reset mode for the tenant.
 * Returns { mode: "admin" | "email" | null }
 */
export async function getResetMode(token) {
  const res = await fetch(`${API_BASE_URL}/users/settings/reset-mode`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load reset mode');
  return res.json();
}

/**
 * setResetMode() — Updates password reset mode for the tenant.
 * mode: "admin" | "email" | null
 */
export async function setResetMode(token, mode) {
  const res = await fetch(`${API_BASE_URL}/users/settings/reset-mode`, {
    method:  'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body:    JSON.stringify({ mode }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || data.error || 'Failed to update reset mode');
  return data;
}

// ─── Usage Analytics (tenant admin) ──────────────────────────────────────────

/**
 * getUsageSummary() — Returns query analytics for the caller's tenant.
 * Calls GET /users/usage/summary — requires admin JWT.
 */
export async function getUsageSummary(token) {
  const res = await fetch(`${API_BASE_URL}/users/usage/summary`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || data.error || 'Failed to load usage summary');
  }
  return res.json();
}


// ─── Support Tickets ──────────────────────────────────────────────────────────

export async function createTicket(ticketData) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/v1/support/tickets`, {
    method: 'POST',
    body:   JSON.stringify(ticketData),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || data.error || 'Failed to create ticket');
  }
  return res.json();
}

export async function listTickets(statusFilter) {
  const url = statusFilter
    ? `${API_BASE_URL}/api/v1/support/tickets?status=${encodeURIComponent(statusFilter)}`
    : `${API_BASE_URL}/api/v1/support/tickets`;
  const res = await fetchWithAuth(url);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || data.error || 'Failed to load tickets');
  }
  return res.json();
}

export async function getTicket(ref) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/v1/support/tickets/${ref}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || data.error || 'Ticket not found');
  }
  return res.json();
}

export async function addTicketComment(ref, body) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/v1/support/tickets/${ref}/comments`, {
    method: 'POST',
    body:   JSON.stringify({ body }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || data.error || 'Failed to add comment');
  }
  return res.json();
}

export async function closeTicket(ref) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/v1/support/tickets/${ref}/close`, {
    method: 'PATCH',
    body:   JSON.stringify({}),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || data.error || 'Failed to close ticket');
  }
  return res.json();
}

export async function uploadTicketAttachment(ref, file) {
  const form = new FormData();
  form.append('file', file);
  // Use raw fetch — fetchWithAuth sets Content-Type: application/json which breaks multipart
  const token = _accessToken;
  const res = await fetch(`${API_BASE_URL}/api/v1/support/tickets/${ref}/attachments`, {
    method:  'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body:    form,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || data.error || 'Failed to upload file');
  }
  return res.json();
}


export async function deleteTicketAttachment(ref, attId) {
  const res = await fetchWithAuth(
    `${API_BASE_URL}/api/v1/support/tickets/${ref}/attachments/${attId}`,
    { method: 'DELETE' },
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || data.error || 'Failed to delete attachment');
  }
  return res.json();
}

// ─── Helper ───────────────────────────────────────────────────────────────────

/** Promisified timeout — used to simulate network latency in mock mode */
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
